import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Field, Input, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import { CheckPanel } from './CheckPanel';
import type { Document, Formulation, Ingredient } from '../../lib/types';

const FIELDS: (keyof Ingredient)[] = [
  'name',
  'commonName',
  'casNumber',
  'amountPerServing',
  'unit',
  'form',
  'function',
  'source',
];
const blank = (): Ingredient => ({
  name: '',
  commonName: '',
  casNumber: '',
  amountPerServing: 0,
  unit: 'mg',
  form: '',
  function: 'active',
  source: '',
});

export function FormulationTab({
  orgId,
  pid,
  canEdit,
  canAccept,
  documents,
  onChanged,
}: {
  orgId: string;
  pid: string;
  canEdit: boolean;
  canAccept: boolean;
  documents: Document[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const base = `/api/v1/orgs/${orgId}/products/${pid}/formulations`;
  const [versions, setVersions] = useState<Formulation[]>([]);
  const [units, setUnits] = useState<string[]>(['mg', 'mcg', 'g', 'IU']);
  const [canonicalNutrients, setCanonicalNutrients] = useState<{ name: string; unit: string }[]>([]);
  const [rows, setRows] = useState<Ingredient[]>([]);
  const [sourceDocId, setSourceDocId] = useState<string | null>(null);
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false); // load() must see the latest value without re-creating itself (stale closure)
  const setDirty = (v: boolean) => {
    dirtyRef.current = v;
    setDirtyState(v);
  };
  const [msg, setMsg] = useState<{ tone: 'red' | 'green' | 'amber'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [imp, setImp] = useState<{
    docId: string;
    headers: string[];
    rows: string[][];
    totalRows: number;
    mapping: Record<string, number | ''>;
    defaults: { unit: string; function: 'active' | 'excipient' };
  } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importDoc, setImportDoc] = useState('');
  const [viewVersion, setViewVersion] = useState<number | null>(null);

  const load = useCallback(
    async (opts: { resetRows?: boolean } = {}) => {
      const [r, nm] = await Promise.all([
        api<{ formulations: Formulation[]; units: string[] }>(base),
        api<{ canonicalNutrients: { name: string; unit: string }[] }>(
          `/api/v1/orgs/${orgId}/nutrients/mappings`,
        ).catch(() => ({ canonicalNutrients: [] })),
      ]);
      setVersions(r.formulations);
      setUnits(r.units);
      if (nm.canonicalNutrients.length) setCanonicalNutrients(nm.canonicalNutrients);
      setViewVersion((v) => v ?? r.formulations[0]?.version ?? null);
      if (
        (opts.resetRows || !dirtyRef.current) &&
        r.formulations[0] &&
        (opts.resetRows || rowsRef.current.length === 0)
      ) {
        setRows(r.formulations[0].ingredients.map((x) => ({ ...x })));
      }
    },
    [base, orgId],
  );
  const rowsRef = useRef<Ingredient[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  useEffect(() => {
    void load();
  }, [load]);

  const fail = (e: unknown) =>
    setMsg({
      tone: 'red',
      text:
        e instanceof ApiError
          ? t([`form.err_${e.code}`, 'form.err_generic'], { code: e.code }) + ` ${e.message}`
          : String(e),
    });
  const edit = (i: number, k: keyof Ingredient, v: string) => {
    setDirty(true);
    setRows((rs) =>
      rs.map((r, j) => (j === i ? { ...r, [k]: k === 'amountPerServing' ? Number(v) : v } : r)),
    );
  };
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const clean = rows.map((r) => ({
        ...r,
        commonName: r.commonName || null,
        casNumber: r.casNumber || null,
        form: r.form || null,
        source: r.source || null,
        nutrient: r.nutrient || null,
        factor: r.factor || null,
      }));
      const f = await api<Formulation>(base, { method: 'POST', json: { ingredients: clean, sourceDocId } });
      setMsg({ tone: 'green', text: t('form.saved', { v: f.version }) });
      setDirty(false);
      setSourceDocId(null);
      setViewVersion(f.version);
      await load({ resetRows: true });
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const parse = async () => {
    setMsg(null);
    try {
      const r = await api<{
        docId: string;
        headers: string[];
        rows: string[][];
        totalRows: number;
        suggestedMapping: Record<string, number>;
      }>(`${base}/parse`, { method: 'POST', json: { docId: importDoc } });
      const mapping: Record<string, number | ''> = {};
      for (const f of FIELDS) mapping[f] = r.suggestedMapping[f] ?? '';
      setImp({
        docId: r.docId,
        headers: r.headers,
        rows: r.rows,
        totalRows: r.totalRows,
        mapping,
        defaults: { unit: 'mg', function: 'active' },
      });
    } catch (e) {
      fail(e);
    }
  };
  const applyMapping = async () => {
    if (!imp) return;
    setMsg(null);
    try {
      const mapping = Object.fromEntries(Object.entries(imp.mapping).filter(([, v]) => v !== '')) as Record<
        string,
        number
      >;
      const r = await api<{ ingredients: Ingredient[]; problems: string[] }>(`${base}/map`, {
        method: 'POST',
        json: { headers: imp.headers, rows: imp.rows, mapping, defaults: imp.defaults },
      });
      setRows(
        r.ingredients.map((x) => ({
          ...x,
          commonName: x.commonName ?? '',
          casNumber: x.casNumber ?? '',
          form: x.form ?? '',
          source: x.source ?? '',
        })),
      );
      setSourceDocId(imp.docId);
      setDirty(true);
      setImportOpen(false);
      setImp(null);
      setMsg(
        r.problems.length
          ? { tone: 'amber', text: `${t('form.problems')} ${r.problems.join('; ')}` }
          : { tone: 'green', text: `${r.ingredients.length} rows` },
      );
    } catch (e) {
      fail(e);
    }
  };
  const sheets = documents.filter((d) => ['.xlsx', '.csv'].includes(d.ext));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{t('form.title')}</h2>
        {versions.map((v) => (
          <Button
            key={v.version}
            variant="secondary"
            className="text-xs"
            onClick={() => {
              setRows(v.ingredients.map((x) => ({ ...x })));
              setDirty(false);
              setSourceDocId(null);
            }}
          >
            {t('form.version', { v: v.version })}
            {v.version === versions[0]?.version && (
              <span className="ml-1 text-emerald-700">({t('form.current')})</span>
            )}
          </Button>
        ))}
        {canEdit && (
          <Button variant="secondary" className="ml-auto" onClick={() => setImportOpen((v) => !v)}>
            {t('form.import')}
          </Button>
        )}
      </div>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {importOpen && canEdit && (
        <Card title={t('form.import')}>
          <p className="mb-2 text-sm text-slate-600">{t('form.importHint')}</p>
          <div className="flex gap-2">
            <Select value={importDoc} onChange={(e) => setImportDoc(e.target.value)}>
              <option value="">-</option>
              {sheets.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.id} {d.name}
                  {d.ext}
                </option>
              ))}
            </Select>
            <Button onClick={() => void parse()} disabled={!importDoc}>
              {t('form.parse')}
            </Button>
          </div>
          {imp && (
            <div className="mt-4 space-y-3">
              <h3 className="text-sm font-medium">
                {t('form.mapping')} ({imp.totalRows} rows)
              </h3>
              <div className="grid gap-2 md:grid-cols-4">
                {FIELDS.map((f) => (
                  <Field
                    key={f}
                    label={t(
                      `form.${f === 'name' ? 'ingredient' : f === 'casNumber' ? 'cas' : f === 'amountPerServing' ? 'amount' : f === 'form' ? 'formCol' : f}`,
                    )}
                  >
                    <Select
                      value={imp.mapping[f] ?? ''}
                      onChange={(e) =>
                        setImp({
                          ...imp,
                          mapping: {
                            ...imp.mapping,
                            [f]: e.target.value === '' ? '' : Number(e.target.value),
                          },
                        })
                      }
                    >
                      <option value="">{t('form.unmapped')}</option>
                      {imp.headers.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `(col ${i + 1})`}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ))}
                <Field label={t('form.defaultUnit')}>
                  <Select
                    value={imp.defaults.unit}
                    onChange={(e) => setImp({ ...imp, defaults: { ...imp.defaults, unit: e.target.value } })}
                  >
                    {units.map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('form.defaultFunction')}>
                  <Select
                    value={imp.defaults.function}
                    onChange={(e) =>
                      setImp({
                        ...imp,
                        defaults: { ...imp.defaults, function: e.target.value as 'active' | 'excipient' },
                      })
                    }
                  >
                    <option value="active">{t('form.active')}</option>
                    <option value="excipient">{t('form.excipient')}</option>
                  </Select>
                </Field>
              </div>
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="text-xs">
                  <thead>
                    <tr>
                      {imp.headers.map((h, i) => (
                        <th key={i} className="px-2 py-1 text-left">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {imp.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {imp.headers.map((_, j) => (
                          <td key={j} className="px-2 py-1">
                            {r[j]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={() => void applyMapping()}>{t('form.apply')}</Button>
            </div>
          )}
        </Card>
      )}
      <Card>
        {rows.length === 0 && <p className="text-sm text-slate-500">{t('form.noVersions')}</p>}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2">{t('form.ingredient')}</th>
                <th>{t('form.commonName')}</th>
                <th>{t('form.cas')}</th>
                <th>{t('form.amount')}</th>
                <th>{t('form.unit')}</th>
                <th>{t('form.formCol')}</th>
                <th>{t('form.function')}</th>
                <th>{t('form.nutrientCol', 'Nutrient (DV)')}</th>
                {canEdit && <th></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={i}>
                  {canEdit ? (
                    <>
                      <td className="py-1">
                        <Input value={r.name} onChange={(e) => edit(i, 'name', e.target.value)} />
                      </td>
                      <td>
                        <Input
                          value={r.commonName ?? ''}
                          onChange={(e) => edit(i, 'commonName', e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          value={r.casNumber ?? ''}
                          onChange={(e) => edit(i, 'casNumber', e.target.value)}
                          className="w-28"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          step="any"
                          value={r.amountPerServing}
                          onChange={(e) => edit(i, 'amountPerServing', e.target.value)}
                          className="w-28"
                        />
                      </td>
                      <td>
                        <Select value={r.unit} onChange={(e) => edit(i, 'unit', e.target.value)}>
                          {units.map((u) => (
                            <option key={u}>{u}</option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <Input value={r.form ?? ''} onChange={(e) => edit(i, 'form', e.target.value)} />
                      </td>
                      <td>
                        <Select value={r.function} onChange={(e) => edit(i, 'function', e.target.value)}>
                          <option value="active">{t('form.active')}</option>
                          <option value="excipient">{t('form.excipient')}</option>
                        </Select>
                      </td>
                      <td>
                        <Select
                          value={r.nutrient ?? ''}
                          onChange={(e) => edit(i, 'nutrient', e.target.value)}
                          className="w-36 text-xs"
                        >
                          <option value="">{t('form.autoResolve', 'Auto-resolve')}</option>
                          {canonicalNutrients.map((c) => (
                            <option key={c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          className="text-xs"
                          onClick={() => {
                            setDirty(true);
                            setRows(rows.filter((_, j) => j !== i));
                          }}
                        >
                          {t('form.remove')}
                        </Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-1">{r.name}</td>
                      <td>{r.commonName}</td>
                      <td className="font-mono text-xs">{r.casNumber}</td>
                      <td>{r.amountPerServing}</td>
                      <td>{r.unit}</td>
                      <td>{r.form}</td>
                      <td>
                        <Badge tone={r.function === 'active' ? 'blue' : 'slate'}>
                          {t(`form.${r.function}`)}
                        </Badge>
                      </td>
                      <td>
                        {r.nutrient ? (
                          <Badge tone="green">{r.nutrient}</Badge>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canEdit && (
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDirty(true);
                setRows([...rows, blank()]);
              }}
            >
              {t('form.addRow')}
            </Button>
            <Button onClick={() => void save()} disabled={busy || rows.length === 0 || !dirty}>
              {busy ? t('form.saving') : t('form.save')}
            </Button>
            {sourceDocId && (
              <span className="text-xs text-slate-500">{t('form.source', { id: sourceDocId })}</span>
            )}
          </div>
        )}
      </Card>
      {viewVersion !== null && (
        <CheckPanel
          orgId={orgId}
          pid={pid}
          version={viewVersion}
          canVerify={canEdit}
          canOverride={canEdit}
          canAccept={canAccept}
        />
      )}
    </div>
  );
}
